/**
 * FlowWebhookDetailDialog - Modal de detalhes do webhook de um flow
 * 
 * Exibe 3 abas:
 * - Geral: Configuração do webhook (FlowWebhookTriggerConfig)
 * - Logs: Histórico de execuções
 * - Estatísticas: Métricas e gráfico de volume
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Webhook,
  Activity, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Percent,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  Eye,
  Save,
  Play,
  AlertCircle,
  AlertTriangle,
  HelpCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { WebhookVolumeChart } from '@/components/crm/WebhookVolumeChart';
import { FlowWebhookTriggerConfig, FlowWebhookTriggerConfigRef } from './config/FlowWebhookTriggerConfig';
import { WebhookHelpDialog } from './WebhookHelpDialog';
import { useFlowWebhooks } from '@/hooks/useFlowWebhooks';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import type { Tables } from '@/integrations/supabase/types';
import type { WebhookTriggerConfig, TriggerConfig } from '@/types/flows';
import { ImportPreviewMetricCard } from '@/components/imports/shared/ImportPreviewMetricCard';
import { ImportPreviewStatusDot } from '@/components/imports/shared/ImportPreviewStatusDot';
import { TooltipProvider } from '@/components/ui/tooltip';

// Logger para depuração
const logger = createLogger('FlowWebhookDetailDialog');

// ============= INTERFACES =============

interface FlowWebhookDetailDialogProps {
  /** Se o modal está aberto */
  open: boolean;
  /** Callback para fechar o modal */
  onOpenChange: (open: boolean) => void;
  /** Flow selecionado */
  flow: Tables<'flows'> | null;
}

// ============= COMPONENTES AUXILIARES =============

// Cards de métrica agora reutilizam o padrão `ImportPreviewMetricCard`
// (mesmo componente usado no modal de detalhes de importação de listas),
// importado abaixo do arquivo.


/**
 * Badge de status do log
 */
function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { label: string; className: string }> = {
    success: { label: 'Sucesso', className: 'bg-green-500/10 text-green-600 border-green-200' },
    error: { label: 'Erro', className: 'bg-red-500/10 text-red-600 border-red-200' },
    validation_error: { label: 'Validação', className: 'bg-amber-500/10 text-amber-600 border-amber-200' },
    auth_error: { label: 'Auth', className: 'bg-red-500/10 text-red-600 border-red-200' },
    duplicate: { label: 'Duplicado', className: 'bg-muted text-muted-foreground' },
  };

  const config = statusConfig[status] || { label: status, className: '' };

  return (
    <Badge variant="outline" className={`text-xs ${config.className}`}>
      {config.label}
    </Badge>
  );
}

/**
 * Badge de modo do webhook
 */
function ModeBadge({ mode }: { mode: string }) {
  if (mode === 'conversation') {
    return (
      <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-200">
        Conversa
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-200">
      Negócios
    </Badge>
  );
}

// ============= COMPONENTE PRINCIPAL =============

export function FlowWebhookDetailDialog({
  open,
  onOpenChange,
  flow,
}: FlowWebhookDetailDialogProps) {
  const queryClient = useQueryClient();
  
  // =========================================================================
  // REF PARA SINCRONIZAÇÃO IMPERATIVA DE VARIÁVEIS
  // Permite chamar syncVariables() no componente filho antes de salvar
  // =========================================================================
  const webhookConfigRef = useRef<FlowWebhookTriggerConfigRef>(null);
  
  // Estado local
  const [activeTab, setActiveTab] = useState('general');
  const [helpOpen, setHelpOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Configuração local do webhook
  const [webhookConfig, setWebhookConfig] = useState<WebhookTriggerConfig>({
    type: 'webhook',
    mode: 'conversation',
    field_mapping: {},
  });

  // =========================================================================
  // FUNÇÕES AUXILIARES PARA GERAÇÃO DINÂMICA DO PAYLOAD DE TESTE
  // =========================================================================

  /**
   * Insere valor em objeto usando dot notation
   * Ex: setNestedValue(obj, 'dados.produto', 'Curso') => obj.dados.produto = 'Curso'
   */
  const setNestedValue = (obj: Record<string, unknown>, path: string, value: unknown): void => {
    const parts = path.split('.');
    let current = obj;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {};
      }
      current = current[parts[i]] as Record<string, unknown>;
    }
    
    current[parts[parts.length - 1]] = value;
  };

  /**
   * Gera valor de exemplo baseado no nome da variável
   * Detecta padrões comuns (cpf, email, link, etc.) e gera valores apropriados
   */
  const generateExampleValue = (varName: string): string => {
    const name = varName.toLowerCase();
    
    // Padrões comuns para geração de valores inteligentes
    if (name.includes('link') || name.includes('url')) return 'https://exemplo.com/link-teste';
    if (name.includes('cpf')) return '12345678900';
    if (name.includes('cnpj')) return '12345678000190';
    if (name.includes('email')) return 'teste@exemplo.com';
    if (name.includes('telefone') || name.includes('phone') || name.includes('celular')) return '5511999999999';
    if (name.includes('valor') || name.includes('preco') || name.includes('amount') || name.includes('price')) return '199.90';
    if (name.includes('data') || name.includes('date')) return '2026-01-31';
    if (name.includes('empresa') || name.includes('company')) return 'Empresa Teste';
    if (name.includes('nome') || name.includes('name')) return 'Teste';
    if (name.includes('contrato') || name.includes('contract')) return 'https://exemplo.com/contrato-123';
    if (name.includes('produto') || name.includes('product')) return 'Produto Exemplo';
    if (name.includes('servico') || name.includes('service')) return 'Serviço Exemplo';
    if (name.includes('endereco') || name.includes('address')) return 'Rua Exemplo, 123';
    if (name.includes('cidade') || name.includes('city')) return 'São Paulo';
    if (name.includes('estado') || name.includes('state')) return 'SP';
    if (name.includes('cep') || name.includes('zipcode')) return '01310100';
    
    // Fallback genérico - usa o nome da variável
    return `valor_${varName}`;
  };

  /**
   * Gera payload de teste baseado nos mapeamentos configurados
   * Inclui campos obrigatórios (telefone, nome, email) + variáveis customizadas
   */
  const generateTestPayload = useCallback((config: WebhookTriggerConfig): string => {
    const payload: Record<string, unknown> = {};
    
    // ========================================
    // CAMPOS OBRIGATÓRIOS DO FIELD_MAPPING
    // ========================================
    const phoneField = config.field_mapping?.contact_phone || 'telefone';
    const nameField = config.field_mapping?.contact_name || 'nome';
    const emailField = config.field_mapping?.contact_email || 'email';
    
    // Inserir no path correto (suporta dot notation)
    setNestedValue(payload, phoneField, '5562993252289');
    setNestedValue(payload, nameField, 'Wenisteen');
    setNestedValue(payload, emailField, 'teste@exemplo.com');
    
    // ========================================
    // VARIÁVEIS CUSTOMIZADAS DO VARIABLE_MAPPING
    // ========================================
    if (config.variable_mapping) {
      for (const [varName, path] of Object.entries(config.variable_mapping)) {
        if (path && typeof path === 'string' && path.trim()) {
          // Gerar valor de exemplo baseado no nome da variável
          const exampleValue = generateExampleValue(varName);
          setNestedValue(payload, path, exampleValue);
        }
      }
    }
    
    return JSON.stringify(payload, null, 2);
  }, []);

  // Payload de teste - estado inicial será gerado dinamicamente
  const [testPayload, setTestPayload] = useState<string>('');
  
  // Estado do resultado do teste
  const [testResult, setTestResult] = useState<{
    success: boolean;
    status: number;
    data: unknown;
    time: number;
  } | null>(null);
  
  const pageSize = 10;

  // Hook de dados de webhook
  const {
    logs,
    logsTotal,
    isLoadingLogs,
    fetchLogs,
    stats,
    isLoadingStats,
    fetchStats,
    volumeData,
    isLoadingVolume,
    fetchVolume,
    regenerateToken,
    isRegenerating,
    testWebhook,
    isTesting,
  } = useFlowWebhooks();

  /**
   * Extrai configuração de webhook do trigger_config
   */
  const getWebhookConfigFromFlow = useCallback((flowData: Tables<'flows'>): WebhookTriggerConfig => {
    // Trigger config pode ser array de triggers ou objeto vazio
    const triggers = flowData.trigger_config as unknown as Array<Record<string, unknown>> | null;
    if (!Array.isArray(triggers)) {
      return { type: 'webhook', mode: 'conversation', field_mapping: {} };
    }
    const webhookTrigger = triggers.find(t => t.type === 'webhook');
    if (webhookTrigger && 'mode' in webhookTrigger) {
      return {
        type: 'webhook',
        mode: (webhookTrigger.mode as 'conversation' | 'deal') || 'conversation',
        field_mapping: (webhookTrigger.field_mapping as Record<string, string>) || {},
        variable_mapping: webhookTrigger.variable_mapping as Record<string, string> | undefined,
        inbox_id: webhookTrigger.inbox_id as string | undefined,
        inbox_name: webhookTrigger.inbox_name as string | undefined,
        workspace_id: webhookTrigger.workspace_id as string | undefined,
        workspace_name: webhookTrigger.workspace_name as string | undefined,
        pipeline_id: webhookTrigger.pipeline_id as string | undefined,
        pipeline_name: webhookTrigger.pipeline_name as string | undefined,
        phase_id: webhookTrigger.phase_id as string | undefined,
        phase_name: webhookTrigger.phase_name as string | undefined,
        default_source: webhookTrigger.default_source as string | undefined,
        seller_id: webhookTrigger.seller_id as string | undefined,
        seller_name: webhookTrigger.seller_name as string | undefined,
        require_auth: webhookTrigger.require_auth as boolean | undefined,
        auth_header_name: webhookTrigger.auth_header_name as string | undefined,
        auth_header_value: webhookTrigger.auth_header_value as string | undefined,
      };
    }
    return { type: 'webhook', mode: 'conversation', field_mapping: {} };
  }, []);

  // Carregar dados ao abrir/mudar de flow
  useEffect(() => {
    if (open && flow) {
      // Carregar config do webhook
      const config = getWebhookConfigFromFlow(flow);
      setWebhookConfig(config);
      
      // Carregar estatísticas e logs
      fetchStats(flow.id);
      fetchVolume(flow.id, 24);
      fetchLogs(flow.id, 1, pageSize);
      setCurrentPage(1);
      setActiveTab('general');
      
      // Limpar resultado anterior do teste
      setTestResult(null);
      
      // Gerar payload de teste dinâmico baseado nas variáveis configuradas
      setTestPayload(generateTestPayload(config));
    }
  }, [open, flow, fetchStats, fetchVolume, fetchLogs, getWebhookConfigFromFlow]);

  // =========================================================================
  // REGENERAR PAYLOAD QUANDO CONFIGURAÇÃO DE VARIÁVEIS MUDA
  // =========================================================================
  // Este useEffect atualiza o payload de teste automaticamente quando o usuário
  // adiciona, remove ou modifica variáveis customizadas no mapeamento
  useEffect(() => {
    if (open && webhookConfig) {
      setTestPayload(generateTestPayload(webhookConfig));
    }
  }, [open, webhookConfig, generateTestPayload]);

  // Recarregar logs quando filtro ou página muda
  useEffect(() => {
    if (open && flow) {
      fetchLogs(flow.id, currentPage, pageSize, statusFilter === 'all' ? undefined : statusFilter);
    }
  }, [currentPage, statusFilter, open, flow, fetchLogs]);

  // Total de páginas
  const totalPages = Math.ceil(logsTotal / pageSize);

  // Handlers
  const handleRefresh = () => {
    if (!flow) return;
    fetchStats(flow.id);
    fetchVolume(flow.id, 24);
    fetchLogs(flow.id, currentPage, pageSize, statusFilter === 'all' ? undefined : statusFilter);
  };

  /**
   * Salvar configuração do webhook
   * 
   * Salva o trigger_config e gera token automaticamente se não existir.
   */
  const handleSave = async () => {
    if (!flow) return;
    
    // =====================================================
    // SINCRONIZAR VARIÁVEIS ANTES DE SALVAR
    // Garante que variáveis editadas sejam incluídas
    // mesmo se o usuário não saiu do input (onBlur)
    // =====================================================
    webhookConfigRef.current?.syncVariables();
    
    // Pequeno delay para garantir que o estado foi atualizado pelo React
    await new Promise(resolve => setTimeout(resolve, 50));
    
    setIsSaving(true);
    try {
      // Obter triggers existentes como array genérico
      const existingTriggers = flow.trigger_config as unknown as Array<Record<string, unknown>> | null;
      const triggers: Array<Record<string, unknown>> = Array.isArray(existingTriggers) ? [...existingTriggers] : [];
      
      // Atualizar ou adicionar webhook trigger
      const webhookIndex = triggers.findIndex(t => t.type === 'webhook');
      if (webhookIndex >= 0) {
        triggers[webhookIndex] = webhookConfig as unknown as Record<string, unknown>;
      } else {
        triggers.push(webhookConfig as unknown as Record<string, unknown>);
      }
      
      // Salvar no banco
      const { error } = await supabase
        .from('flows')
        .update({ trigger_config: triggers as unknown as Tables<'flows'>['trigger_config'] })
        .eq('id', flow.id);
      
      if (error) throw error;
      
      // ========================================
      // Gerar token automaticamente se não existir
      // ========================================
      if (!flow.webhook_token) {
        logger.info('Token do webhook não existe, gerando automaticamente...', flow.id);
        const newToken = await regenerateToken(flow.id);
        if (newToken) {
          logger.info('Token gerado com sucesso:', newToken.substring(0, 10) + '...');
        } else {
          logger.warn('Falha ao gerar token automaticamente');
          toast.warning('Configuração salva, mas erro ao gerar token. Clique em regenerar.');
        }
      }
      
      // Invalidar cache para atualizar UI
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      
      toast.success('Configuração salva com sucesso!');
    } catch (error) {
      logger.error('Erro ao salvar configuração', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Regenerar token do webhook
   */
  const handleRegenerateToken = async () => {
    if (!flow) return;
    const newToken = await regenerateToken(flow.id);
    if (newToken) {
      queryClient.invalidateQueries({ queryKey: ['flows'] });
    }
  };

  /**
   * Testar webhook
   * Executa teste e armazena resultado para exibição na UI
   */
  const handleTest = async () => {
    if (!flow?.webhook_token) {
      toast.error('Token do webhook não configurado');
      return;
    }

    try {
      const payload = JSON.parse(testPayload);
      // Capturar e armazenar resultado do teste
      const result = await testWebhook(flow.webhook_token, payload);
      setTestResult(result);
      
      // Atualizar logs após o teste
      setTimeout(() => {
        handleRefresh();
      }, 1000);
    } catch {
      toast.error('Payload JSON inválido');
    }
  };

  // Formatar taxa de sucesso
  const successRate = stats?.successRate 
    ? `${stats.successRate.toFixed(1)}%` 
    : '0%';

  // Formatar última requisição
  const lastRequestText = stats?.lastRequestAt
    ? formatDistanceToNow(new Date(stats.lastRequestAt), { 
        addSuffix: true, 
        locale: ptBR 
      })
    : 'Nunca';

  if (!flow) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5 text-rose-500" />
            <span>Webhook de Integração: {flow.name}</span>
            {/* Botão de ajuda — abre guia explicativo com exemplos */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground ml-1"
              onClick={() => setHelpOpen(true)}
              title="Como usar webhooks"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Configure, monitore e teste o webhook deste fluxo.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="general">Geral</TabsTrigger>
              <TabsTrigger value="logs">Logs</TabsTrigger>
              <TabsTrigger value="stats">Estatísticas</TabsTrigger>
            </TabsList>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </div>

          {/* Aba Geral - Configuração */}
          <TabsContent value="general" className="flex-1 overflow-auto mt-4 space-y-4">
            <ScrollArea className="flex-1 pr-4">
              <FlowWebhookTriggerConfig
                ref={webhookConfigRef}
                config={webhookConfig}
                onChange={setWebhookConfig}
                webhookToken={flow.webhook_token}
                onRegenerateToken={handleRegenerateToken}
                isRegenerating={isRegenerating}
              />

              {/* Seção de Teste */}
              <div className="mt-6 space-y-3 p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2">
                  <Play className="h-4 w-4 text-muted-foreground" />
                  <Label>Testar Webhook</Label>
                </div>

                {/* Alerta: Flow não está ativo/publicado */}
                {flow.status !== 'active' && (
                  <Alert variant="destructive" className="mb-3">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Flow não publicado</AlertTitle>
                    <AlertDescription>
                      O flow está em <strong>rascunho</strong>. Publique o flow (status "Ativo") 
                      para que o webhook aceite requisições. Testes só funcionam com flows publicados.
                    </AlertDescription>
                  </Alert>
                )}
                
                <Textarea
                  placeholder='{"telefone": "5511999999999", "nome": "Teste"}'
                  value={testPayload}
                  onChange={(e) => setTestPayload(e.target.value)}
                  className="font-mono text-sm h-32"
                  disabled={flow.status !== 'active'}
                />
                
                <div className="flex gap-2">
                  <Button
                    onClick={handleTest}
                    disabled={isTesting || !flow.webhook_token || flow.status !== 'active'}
                    className="flex-1"
                    title={
                      isTesting 
                        ? 'Teste em andamento...' 
                        : !flow.webhook_token 
                          ? 'Salve a configuração para gerar o token' 
                          : flow.status !== 'active' 
                            ? 'Publique o flow para testar o webhook' 
                            : 'Enviar requisição de teste'
                    }
                  >
                    {isTesting ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    {isTesting ? 'Testando...' : 'Enviar Teste'}
                  </Button>
                </div>

                {/* Resultado do teste */}
                {testResult && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30 mt-3">
                    {/* Status */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {testResult.success ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <span className="font-medium">
                          {testResult.success ? 'Sucesso' : 'Erro'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          HTTP {testResult.status}
                        </Badge>
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {testResult.time}ms
                        </Badge>
                      </div>
                    </div>

                    {/* Resposta */}
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase">
                        Resposta
                      </p>
                      <pre className="text-xs bg-background rounded-lg p-3 overflow-auto max-h-32 border break-all whitespace-pre-wrap">
                        {JSON.stringify(testResult.data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Alerta: Token não gerado */}
                {!flow.webhook_token && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Salve a configuração para gerar o token do webhook.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Botão Salvar */}
              <div className="flex justify-end mt-6">
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {isSaving ? 'Salvando...' : 'Salvar Configuração'}
                </Button>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Aba de Logs */}
          <TabsContent value="logs" className="flex-1 overflow-hidden flex flex-col mt-4">
            {/* Filtros */}
            <div className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por telefone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="success">Sucesso</SelectItem>
                  <SelectItem value="error">Erro</SelectItem>
                  <SelectItem value="validation_error">Validação</SelectItem>
                  <SelectItem value="auth_error">Auth</SelectItem>
                  <SelectItem value="duplicate">Duplicado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tabela de logs — padrão visual do modal de preview de listas */}
            <TooltipProvider delayDuration={150}>
              <div className="flex-1 rounded-md border overflow-hidden flex flex-col min-h-0">
                <div className="flex-1 overflow-auto">
                  {isLoadingLogs ? (
                    <div className="space-y-px">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Skeleton key={i} className="h-10 w-full rounded-none" />
                      ))}
                    </div>
                  ) : logs.length === 0 ? (
                    <div className="rounded-md border-0 border-dashed py-10 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                      <Activity className="h-8 w-8 opacity-40" />
                      <p className="text-sm">Nenhum log encontrado</p>
                      <p className="text-xs">As execuções do webhook aparecerão aqui.</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-muted/60 backdrop-blur supports-[backdrop-filter]:bg-muted/50">
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="h-9 w-10 text-xs">#</TableHead>
                          <TableHead className="h-9 text-xs w-32">Data/Hora</TableHead>
                          <TableHead className="h-9 text-xs w-24">Modo</TableHead>
                          <TableHead className="h-9 text-xs w-28">Status</TableHead>
                          <TableHead className="h-9 text-xs">Telefone</TableHead>
                          <TableHead className="h-9 text-xs w-24 text-right">Tempo</TableHead>
                          <TableHead className="h-9 text-xs w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {logs
                          .filter(log =>
                            !searchTerm ||
                            log.contact_phone?.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .map((log, idx) => {
                            // Mapeamento status → cor + label para ImportPreviewStatusDot
                            const STATUS_MAP: Record<string, { color: string; label: string }> = {
                              success: { color: 'bg-green-500', label: 'Sucesso' },
                              error: { color: 'bg-red-500', label: 'Erro' },
                              auth_error: { color: 'bg-red-500', label: 'Auth' },
                              validation_error: { color: 'bg-amber-500', label: 'Validação' },
                              duplicate: { color: 'bg-muted-foreground', label: 'Duplicado' },
                            };
                            const statusCfg = STATUS_MAP[log.status] ?? {
                              color: 'bg-muted-foreground',
                              label: log.status,
                            };
                            // Número global da página atual
                            const rowNumber = (currentPage - 1) * pageSize + idx + 1;

                            return (
                              <React.Fragment key={log.id}>
                                <TableRow
                                  className="cursor-pointer hover:bg-muted/50 text-xs"
                                  onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                                >
                                  <TableCell className="py-2 text-muted-foreground tabular-nums">
                                    {rowNumber}
                                  </TableCell>
                                  <TableCell className="py-2 font-mono tabular-nums whitespace-nowrap">
                                    {format(new Date(log.created_at), 'dd/MM HH:mm:ss', { locale: ptBR })}
                                  </TableCell>
                                  <TableCell className="py-2">
                                    <ModeBadge mode={log.mode} />
                                  </TableCell>
                                  <TableCell className="py-2">
                                    <ImportPreviewStatusDot
                                      colorClass={statusCfg.color}
                                      label={statusCfg.label}
                                    />
                                  </TableCell>
                                  <TableCell className="py-2 font-mono tabular-nums">
                                    {log.contact_phone || (
                                      <span className="text-muted-foreground/60">—</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="py-2 text-right text-muted-foreground">
                                    {log.processing_time_ms ? (
                                      `${log.processing_time_ms}ms`
                                    ) : (
                                      <span className="text-muted-foreground/60">—</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="py-2">
                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                  </TableCell>
                                </TableRow>
                                {/* Linha expandida com detalhes (colSpan = 7 com a coluna #) */}
                                {expandedLogId === log.id && (
                                  <TableRow>
                                    <TableCell colSpan={7} className="bg-muted/30 p-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        {/* Informações gerais */}
                                        <div className="space-y-2">
                                          <h4 className="font-medium text-sm">Detalhes</h4>
                                          <div className="text-sm space-y-1">
                                            <p><span className="text-muted-foreground">Contact ID:</span> {log.contact_id || '-'}</p>
                                            {log.mode === 'conversation' && (
                                              <p><span className="text-muted-foreground">Conversa ID:</span> {log.conversation_id || '-'}</p>
                                            )}
                                            {log.mode === 'deal' && (
                                              <p><span className="text-muted-foreground">Deal ID:</span> {log.deal_id || '-'}</p>
                                            )}
                                            <p><span className="text-muted-foreground">Execução ID:</span> {log.execution_id || '-'}</p>
                                            <p><span className="text-muted-foreground">IP:</span> {log.request_ip || '-'}</p>
                                          </div>
                                          {log.error_message && (
                                            <div className="mt-2 p-2 bg-destructive/10 rounded border border-destructive/20">
                                              <p className="text-sm font-medium text-destructive">Erro:</p>
                                              <p className="text-sm text-destructive/80">{log.error_message}</p>
                                              {log.error_code && (
                                                <p className="text-xs text-muted-foreground mt-1">Código: {log.error_code}</p>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        {/* Payload */}
                                        <div className="space-y-2">
                                          <h4 className="font-medium text-sm">Payload Recebido</h4>
                                          <ScrollArea className="h-32 border rounded bg-background p-2">
                                            <pre className="text-xs font-mono whitespace-pre-wrap">
                                              {log.request_payload
                                                ? JSON.stringify(log.request_payload, null, 2)
                                                : 'Nenhum payload'}
                                            </pre>
                                          </ScrollArea>
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </TooltipProvider>


            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Mostrando {logs.length} de {logsTotal} registros
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Página {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Aba de Estatísticas */}
          <TabsContent value="stats" className="flex-1 overflow-auto mt-4 space-y-6">
            {isLoadingStats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map(i => (
                    <Skeleton key={i} className="h-24 w-full rounded-xl" />
                  ))}
                </div>
                <Skeleton className="h-64 w-full rounded-xl" />
              </div>
            ) : (
              <>
                {/* Grid de cards de métricas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <ImportPreviewMetricCard
                    icon={<Activity className="h-4 w-4" />}
                    title="Total de Requisições"
                    value={stats?.totalRequests || 0}
                    variant="primary"
                  />
                  <ImportPreviewMetricCard
                    icon={<CheckCircle2 className="h-4 w-4" />}
                    title="Com Sucesso"
                    value={stats?.successfulRequests || 0}
                    variant="success"
                  />
                  <ImportPreviewMetricCard
                    icon={<Percent className="h-4 w-4" />}
                    title="Taxa de Sucesso"
                    value={successRate}
                    variant={
                      (stats?.successRate || 0) >= 80
                        ? 'success'
                        : (stats?.successRate || 0) >= 50
                        ? 'info'
                        : 'danger'
                    }
                  />
                  <ImportPreviewMetricCard
                    icon={<Clock className="h-4 w-4" />}
                    title="Última Requisição"
                    value={lastRequestText}
                    subValue={stats?.avgProcessingTime ? `Média: ${stats.avgProcessingTime}ms` : undefined}
                    variant="info"
                  />

                </div>

                {/* Gráfico de volume */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Volume de Requisições (24h)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isLoadingVolume ? (
                      <Skeleton className="h-48 w-full" />
                    ) : (
                      <WebhookVolumeChart data={volumeData} />
                    )}
                  </CardContent>
                </Card>

                {/* Erros recentes */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-destructive" />
                      Erros Recentes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats?.failedRequests === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        Nenhum erro registrado
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {stats?.failedRequests || 0} erro(s) registrado(s). Veja a aba "Logs" para detalhes.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Modal de ajuda — guia explicativo + 5 exemplos práticos */}
      <WebhookHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </Dialog>
  );
}
